import { createContext, useContext, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { useCopilotSession, CopilotProvider } from '@customerio/hydra-copilot/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { useCopilotSessionAutoName, useCopilotSessionUrlSync } from '../hooks';
import {
  Box,
  Code,
  CodeBlock,
  Divider,
  Emphasis,
  Heading,
  Image,
  Link,
  List,
  ListItem,
  Paragraph,
  Strong,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
} from '@customerio/pluma-components/react';
import type { MarkdownComponents } from '@customerio/hydra-copilot/react';
import { ApprovalComponent } from '../tools/approval';
import { ApprovedResourcesProvider } from '../tools/approval';
import { copilotLibrary, copilotPromptOptions } from './openui-library';
import * as markdownStyles from './markdown.css';

interface CopilotContextValue {
  session: ReturnType<typeof useCopilotSession>;
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
}

const CopilotSessionContext = createContext<CopilotContextValue | null>(null);

export function useCopilotSessionContext() {
  const context = useContext(CopilotSessionContext);
  if (context == null) {
    throw new Error('useCopilotSessionContext must be used within CopilotSessionProvider');
  }
  return context.session;
}

export function useCopilotChatContext() {
  const context = useContext(CopilotSessionContext);
  if (context == null) {
    throw new Error('useCopilotChatContext must be used within CopilotSessionProvider');
  }
  return context;
}

const markdownComponents: MarkdownComponents = {
  p: ({ children }) => (
    <Paragraph className={markdownStyles.paragraph} color="inherit">
      {children}
    </Paragraph>
  ),
  h1: ({ children }) => (
    <Heading level="2" color="inherit">
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading level="3" color="inherit">
      {children}
    </Heading>
  ),
  h3: ({ children }) => (
    <Heading level="4" color="inherit">
      {children}
    </Heading>
  ),
  h4: ({ children }) => (
    <Heading level="5" color="inherit">
      {children}
    </Heading>
  ),
  h5: ({ children }) => (
    <Heading level="6" color="inherit">
      {children}
    </Heading>
  ),
  h6: ({ children }) => (
    <Heading level="6" color="inherit">
      {children}
    </Heading>
  ),
  strong: ({ children }) => <Strong>{children}</Strong>,
  em: ({ children }) => <Emphasis>{children}</Emphasis>,
  pre: ({ children }) => <>{children}</>,
  code: ({ className, children }) => {
    const language = typeof className === 'string' ? className.replace('language-', '') : '';
    const isMultiline = typeof children === 'string' && children.includes('\n');
    const isBlock = language.length > 0 || isMultiline;

    if (isBlock) {
      const code = typeof children === 'string' ? children.replace(/\n$/, '') : String(children);
      return (
        <CodeBlock
          code={code}
          language={(language.length > 0 ? language : 'plain') as Parameters<typeof CodeBlock>[0]['language']}
          mode="dark"
          withCopyButton={true}
          withLanguageLabel={language.length > 0}
        />
      );
    }

    return <Code>{children}</Code>;
  },
  blockquote: ({ children }) => (
    <Box
      pl="200"
      as="blockquote"
      style={{
        borderLeft: `${themeVars.border['width-050']} solid ${themeVars.color['border-minimal']}`,
        marginLeft: 0,
        marginRight: 0,
      }}
    >
      {children}
    </Box>
  ),
  ul: ({ children }) => <List type="unordered">{children}</List>,
  ol: ({ children }) => <List type="ordered">{children}</List>,
  li: ({ children }) => <ListItem>{children}</ListItem>,
  a: ({ href, children }) => (
    <Link href={href} isExternal={true} variant="secondary">
      {children}
    </Link>
  ),
  img: ({ src, alt }) => <Image src={src} alt={alt} style={{ maxWidth: '100%', height: 'auto' }} />,
  hr: () => <Divider my="100" />,
  table: ({ children }) => (
    <Box overflowX="auto">
      <Table caption="Table">{children}</Table>
    </Box>
  ),
  thead: ({ children }) => <TableThead>{children}</TableThead>,
  tbody: ({ children }) => <TableTbody>{children}</TableTbody>,
  tr: ({ children }) => <TableTr>{children}</TableTr>,
  th: ({ children }) => <TableTh>{children}</TableTh>,
  td: ({ children }) => <TableTd>{children}</TableTd>,
};

export function CopilotSessionProvider({ children }: { children: ReactNode }) {
  const session = useCopilotSession({ isPrimary: true });
  const [input, setInput] = useState('');
  const isV2 = useFeatureFlag(AccountFeatureFlag.AiAssistantV2);

  useCopilotSessionAutoName(session);
  useCopilotSessionUrlSync(session, setInput);

  const contextValue: CopilotContextValue = {
    session,
    input,
    setInput,
  };

  return (
    <CopilotSessionContext.Provider value={contextValue}>
      <ApprovedResourcesProvider>
        <CopilotProvider
          markdown={markdownComponents}
          library={isV2 ? copilotLibrary : undefined}
          libraryPromptOptions={isV2 ? copilotPromptOptions : undefined}
          approval={ApprovalComponent}
        >
          {children}
        </CopilotProvider>
      </ApprovedResourcesProvider>
    </CopilotSessionContext.Provider>
  );
}
